import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import client from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useFinancialYear } from "../context/FinancialYearContext";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Pie, Doughnut, PolarArea } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Tooltip,
  Legend,
);

const money = (v) =>
  Number(v || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const num = (v) => {
  const n = Number(v || 0);
  return Number.isFinite(n) ? n : 0;
};

const get = (row, pascal, camel) => row?.[pascal] ?? row?.[camel] ?? "";

const styles = {
  page: { fontSize: 12, padding: 0 },
  hero: {
    border: 0,
    borderRadius: 16,
    overflow: "hidden",
    boxShadow: "0 2px 12px rgba(0,0,0,.08)",
    background: "#fff",
  },
  heroHeader: {
    background: "linear-gradient(90deg,#0b4f4a,#0f766e)",
    color: "#fff",
    padding: "14px 18px",
  },
  card: {
    border: 0,
    borderRadius: 14,
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    background: "#fff",
    height: "100%",
  },
  chartCard: {
    border: 0,
    borderRadius: 16,
    boxShadow: "0 2px 12px rgba(0,0,0,.08)",
    background: "#fff",
    overflow: "hidden",
  },
  chartHeader: {
    background: "linear-gradient(90deg,#0b4f4a,#0f766e)",
    color: "#fff",
    padding: "10px 14px",
    fontWeight: 800,
  },
  label: { fontSize: 11, color: "#6c757d", fontWeight: 700 },
  value: { fontSize: 17, fontWeight: 800 },
};

export default function CompanyReportsDashboardPage() {
  const { user } = useAuth();
  const { financialYear } = useFinancialYear();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [trial, setTrial] = useState([]);
  const [pl, setPl] = useState([]);
  const [bs, setBs] = useState([]);
  const [chartType, setChartType] = useState("bar");

  const companyId = user?.companyId;

  const query = useMemo(() => {
    const q = new URLSearchParams({
      fromDate: financialYear.fromDate,
      toDate: financialYear.toDate,
    });

    if (companyId) q.set("companyId", companyId);
    return q.toString();
  }, [financialYear.fromDate, financialYear.toDate, companyId]);

  const loadReports = async () => {
    setLoading(true);
    setError("");

    try {
      const [tbRes, plRes, bsRes] = await Promise.all([
        client.get(`/finance/trial-balance?${query}`),
        client.get(`/finance/profit-loss?${query}`),
        client.get(`/finance/balance-sheet?${query}`),
      ]);

      setTrial(Array.isArray(tbRes.data) ? tbRes.data : []);
      setPl(Array.isArray(plRes.data) ? plRes.data : []);
      setBs(Array.isArray(bsRes.data) ? bsRes.data : []);
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.message ||
          e?.response?.data ||
          "Failed to load company reports",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const summary = useMemo(() => {
    const totalDebit = trial.reduce(
      (s, r) => s + num(get(r, "TotalDebit", "totalDebit")),
      0,
    );

    const totalCredit = trial.reduce(
      (s, r) => s + num(get(r, "TotalCredit", "totalCredit")),
      0,
    );

    const income = pl
      .filter((x) => get(x, "AccountType", "accountType") === "Income")
      .reduce((s, r) => s + num(get(r, "Amount", "amount")), 0);

    const expense = pl
      .filter((x) => get(x, "AccountType", "accountType") === "Expense")
      .reduce((s, r) => s + num(get(r, "Amount", "amount")), 0);

    const assets = bs
      .filter((x) => get(x, "AccountType", "accountType") === "Asset")
      .reduce((s, r) => s + num(get(r, "Amount", "amount")), 0);

    const liabilities = bs
      .filter((x) => get(x, "AccountType", "accountType") === "Liability")
      .reduce((s, r) => s + num(get(r, "Amount", "amount")), 0);

    const equity = bs
      .filter((x) => get(x, "AccountType", "accountType") === "Equity")
      .reduce((s, r) => s + num(get(r, "Amount", "amount")), 0);

    const liabilitiesEquity = liabilities + equity;
    const tbDifference = totalDebit - totalCredit;
    const bsDifference = assets - liabilitiesEquity;
    const netProfit = income - expense;

    return {
      totalDebit,
      totalCredit,
      tbDifference,
      income,
      expense,
      netProfit,
      assets,
      liabilities,
      equity,
      liabilitiesEquity,
      bsDifference,
      tbBalanced: Math.abs(tbDifference) < 0.01,
      bsBalanced: Math.abs(bsDifference) < 0.01,
    };
  }, [trial, pl, bs]);

  const chartData = {
    labels: ["Income", "Expense", "Assets", "Liabilities", "Equity"],
    datasets: [
      {
        label: "Amount",
        data: [
          summary.income,
          summary.expense,
          summary.assets,
          summary.liabilities,
          summary.equity,
        ],
        backgroundColor: [
          "rgba(25,135,84,.78)",
          "rgba(220,53,69,.78)",
          "rgba(13,110,253,.78)",
          "rgba(255,128,0,.78)",
          "rgba(111,66,193,.78)",
        ],
        borderColor: [
          "rgba(25,135,84,1)",
          "rgba(220,53,69,1)",
          "rgba(13,110,253,1)",
          "rgba(255,128,0,1)",
          "rgba(111,66,193,1)",
        ],
        borderWidth: 1.5,
        borderRadius: chartType === "bar" ? 8 : 0,
        maxBarThickness: 70,
        hoverOffset: 8,
      },
    ],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: { font: { size: 11 }, color: "#495057" },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `₹ ${money(ctx.raw)}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 11 }, color: "#495057" },
      },
      y: {
        beginAtZero: true,
        grid: { color: "rgba(0,0,0,.06)" },
        ticks: {
          font: { size: 11 },
          color: "#495057",
          callback: (value) => `₹ ${Number(value).toLocaleString("en-IN")}`,
        },
      },
    },
  };

  const circleOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          font: { size: 11 },
          color: "#495057",
          usePointStyle: true,
          padding: 14,
        },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.label}: ₹ ${money(ctx.raw)}`,
        },
      },
    },
  };

  const renderChart = () => {
    if (chartType === "pie")
      return <Pie data={chartData} options={circleOptions} />;

    if (chartType === "doughnut")
      return <Doughnut data={chartData} options={circleOptions} />;

    if (chartType === "polar")
      return <PolarArea data={chartData} options={circleOptions} />;

    return <Bar data={chartData} options={barOptions} />;
  };

  return (
    <div className="container-fluid py-2" style={styles.page}>
      <div style={styles.hero} className="mb-3">
        <div style={styles.heroHeader}>
          <div className="d-flex justify-content-between flex-wrap gap-2 align-items-center">
            <div>
              <h5 className="fw-bold mb-1">Company Wise Reports</h5>
              <div style={{ fontSize: 11, opacity: 0.9 }}>
                <strong>{user?.companyName || "Setup required"}</strong> •{" "}
                {financialYear.fromDate} to {financialYear.toDate}
              </div>
              <div style={{ fontSize: 11, opacity: 0.9 }}>
                GSTIN: <strong>{user?.companyGstin || "-"}</strong> • Phone:{" "}
                <strong>{user?.companyPhone || "-"}</strong>
              </div>
            </div>

            <div className="d-flex gap-2 flex-wrap">
              <button
                className="btn btn-light btn-sm fw-bold px-3"
                onClick={loadReports}
                disabled={loading}
              >
                <i className="bi bi-arrow-clockwise me-1"></i>
                {loading ? "Loading..." : "Refresh"}
              </button>

              <Link
                className="btn btn-primary btn-sm fw-bold px-3"
                to="/trial-balance"
              >
                Trial Balance
              </Link>

              <Link
                className="btn btn-outline-light btn-sm fw-bold px-3"
                to="/profit-loss"
              >
                P&L
              </Link>

              <Link
                className="btn btn-outline-light btn-sm fw-bold px-3"
                to="/balance-sheet"
              >
                Balance Sheet
              </Link>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger m-2 py-2" style={{ fontSize: 12 }}>
            {error}
          </div>
        )}
      </div>

      <div className="row g-2 mb-2">
        <Summary
          title="Trial Debit"
          value={summary.totalDebit}
          icon="bi-calculator"
          color="#0d6efd"
        />
        <Summary
          title="Trial Credit"
          value={summary.totalCredit}
          icon="bi-calculator-fill"
          color="#6f42c1"
        />
        <Summary
          title="Trial Status"
          value={
            summary.tbBalanced
              ? "Balanced"
              : `Diff ₹ ${money(Math.abs(summary.tbDifference))}`
          }
          icon={
            summary.tbBalanced
              ? "bi-check-circle"
              : "bi-exclamation-triangle"
          }
          color={summary.tbBalanced ? "#0f766e" : "#dc3545"}
          isText
        />
        <Summary
          title="Net Profit / Loss"
          value={summary.netProfit}
          icon={
            summary.netProfit >= 0 ? "bi-graph-up-arrow" : "bi-graph-down-arrow"
          }
          color={summary.netProfit >= 0 ? "#0f766e" : "#dc3545"}
        />
        <Summary
          title="Balance Sheet"
          value={
            summary.bsBalanced
              ? "Balanced"
              : `Diff ₹ ${money(Math.abs(summary.bsDifference))}`
          }
          icon={
            summary.bsBalanced ? "bi-shield-check" : "bi-shield-exclamation"
          }
          color={summary.bsBalanced ? "#0f766e" : "#dc3545"}
          isText
        />
      </div>

      <div className="row g-2 mb-2">
        <Summary
          title="Income"
          value={summary.income}
          icon="bi-cash-stack"
          color="#0f766e"
        />
        <Summary
          title="Expense"
          value={summary.expense}
          icon="bi-wallet2"
          color="#dc3545"
        />
        <Summary
          title="Assets"
          value={summary.assets}
          icon="bi-building"
          color="#0d6efd"
        />
        <Summary
          title="Liabilities"
          value={summary.liabilities}
          icon="bi-bank"
          color="#ff8000"
        />
        <Summary
          title="Equity"
          value={summary.equity}
          icon="bi-person-badge"
          color="#6f42c1"
        />
      </div>

      <div style={styles.chartCard}>
        <div
          style={{
            ...styles.chartHeader,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
          }}
        >
          <div>
            <i className="bi bi-bar-chart-line me-2"></i>
            Financial Snapshot Graph
          </div>

          <select
            className="form-select form-select-sm"
            style={{ width: 180, fontSize: 11, fontWeight: 700 }}
            value={chartType}
            onChange={(e) => setChartType(e.target.value)}
          >
            <option value="bar">Bar Chart</option>
            <option value="pie">Pie Chart</option>
            <option value="doughnut">Wagon Wheel / Doughnut</option>
            <option value="polar">Polar Area</option>
          </select>
        </div>

        <div style={{ padding: 14, height: 330 }}>{renderChart()}</div>
      </div>
    </div>
  );
}

function Summary({ title, value, icon, color, isText = false }) {
  return (
    <div className="col-md-4 col-xl">
      <div
        className="card border-0 h-100"
        style={{
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: "0 2px 10px rgba(0,0,0,.08)",
          borderTop: `4px solid ${color}`,
          background: "linear-gradient(180deg,#ffffff,#f8f9fa)",
        }}
      >
        <div className="card-body py-3 px-3">
          <div className="d-flex justify-content-between align-items-center">
            <div style={{ flex: 1 }}>
              <div style={styles.label}>{title}</div>
              <div style={{ ...styles.value, color }}>
                {isText ? value : `₹ ${money(value)}`}
              </div>
            </div>

            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: `${color}18`,
                color,
                fontSize: 21,
              }}
            >
              <i className={`bi ${icon}`}></i>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}